import React from "react";
import { Link } from "react-router-dom";
import { getLabel, formatPrice, formatMileage } from "../../utils/formatters";

export default function CarTile({ car }) {
  const placeholderImg = 'https://via.placeholder.com/400x300?text=Brak+foto';

  // Use semantic_url from API or fallback to constructing it
  const linkPath = car.semantic_url || `/ogloszenia/${car.slug || car.id}`;

  return (
    <article className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200 flex flex-col h-full group relative">
      <Link to={linkPath} className="flex flex-col h-full text-gray-900 no-underline">
        {/* Image Container */}
        <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
          <img
            src={car.primary_image || car.image || placeholderImg}
            alt={`${car.make} ${car.model}`}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          {car.is_featured && (
            <div className="absolute top-2 left-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded shadow-sm uppercase tracking-wide">
              Wyróżnione
            </div>
          )}
        </div>

        {/* Content Container */}
        <div className="p-3 md:p-4 flex flex-col flex-grow">
          {/* Title */}
          <h2 className="text-base md:text-lg font-bold text-gray-900 leading-tight mb-1 group-hover:text-red-700 transition-colors">
            {car.make} {car.model}
          </h2>

          {/* Subtitle / Version */}
          <p className="text-xs md:text-sm text-gray-500 mb-3 line-clamp-1">
            {car.year} • {getLabel(car.body_type, 'body_type')}
          </p>

          {/* Specs Grid */}
          <div className="grid grid-cols-2 gap-x-2 gap-y-2 text-xs text-gray-600 mb-4">
            {/* Mileage */}
            <div className="flex items-center gap-1.5" title="Przebieg">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              {formatMileage(car.mileage)}
            </div>

            {/* Fuel Type */}
            <div className="flex items-center gap-1.5" title="Paliwo">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
              <span className="capitalize">{getLabel(car.fuel_type, 'fuel_type')}</span>
            </div>

            {/* Transmission */}
            <div className="flex items-center gap-1.5" title="Skrzynia biegów">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              {getLabel(car.transmission, 'transmission')}
            </div>

            {/* Body Type */}
            <div className="flex items-center gap-1.5" title="Nadwozie">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 18h14M5 18a2 2 0 01-2-2V8a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2M5 18v3h14v-3" />
              </svg>
              {getLabel(car.body_type, 'body_type')}
            </div>

            {/* Capacity */}
            <div className="flex items-center gap-1.5" title="Pojemność">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              {car.capacity ? `${parseFloat(car.capacity).toLocaleString()} cm³` : "-"}
            </div>

            {/* Power */}
            <div className="flex items-center gap-1.5" title="Moc">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              {car.power ? `${car.power} KM` : "-"}
            </div>
          </div>

          {/* Price and Location */}
          <div className="mt-auto border-t border-gray-100 pt-3 flex items-end justify-between">
            <div>
              <span className="block text-xl md:text-2xl font-bold text-red-700 leading-none">
                {formatPrice(car.price)} <span className="text-xs md:text-sm font-normal text-red-600">PLN</span>
              </span>
            </div>
            {/* Heart Icon for "Observe" */}

          </div>

          {/* Location */}
          <div className="flex items-center gap-1 text-xs text-gray-500 mt-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Bakałarzewo
          </div>
        </div>
      </Link>
    </article>
  );
}
